import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../../services/api';
import { TEMPLATES, getTemplate, RENDERERS } from './templates';
import { scoreConcours } from '../../data/concoursScore';
import { useApp } from '../../context/AppContext';
import './LandingBuilder.css';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const PUBLIC_BASE = BASE_URL.replace('/api', '').replace(':8000', '') || window.location.origin;

const FIELD_LABELS = {
  prize_image_url: { label: 'Photo du lot (URL)', type: 'text', placeholder: 'Colle l\'URL d\'une image — ou ajoute la photo depuis l\'assistant' },
  pickup_info:    { label: 'Infos de retrait du lot', type: 'textarea', placeholder: 'Où, quand et comment récupérer le lot (envoyé automatiquement au gagnant)' },
  headline:       { label: 'Titre principal', type: 'text', placeholder: 'Votre titre accrocheur' },
  subheadline:    { label: 'Sous-titre',      type: 'text', placeholder: 'Complément du titre' },
  resource_name:  { label: 'Nom de la ressource', type: 'text', placeholder: 'Ex : Guide complet' },
  offer_name:     { label: 'Nom de l\'offre',  type: 'text', placeholder: 'Ex : Audit marketing offert' },
  discount_text:  { label: 'Texte réduction',  type: 'text', placeholder: 'Ex : -20%' },
  validity_text:  { label: 'Validité offre',   type: 'text', placeholder: 'Ex : Valable jusqu\'au 31 mai' },
  event_name:     { label: 'Nom de l\'événement', type: 'text', placeholder: 'Ex : Atelier Marketing' },
  event_date:     { label: 'Date et heure',    type: 'text', placeholder: 'Ex : 15 juin 2026 — 9h à 12h' },
  event_location: { label: 'Lieu',             type: 'text', placeholder: 'Ex : Paris / En ligne' },
  description:    { label: 'Description',      type: 'textarea', placeholder: 'Quelques phrases de présentation' },
  badge_text:     { label: 'Texte badge',      type: 'text', placeholder: 'Ex : GRATUIT' },
  cta_text:       { label: 'Texte du bouton',  type: 'text', placeholder: 'Ex : Recevoir mon guide gratuit' },
  form_title:     { label: 'Titre du formulaire', type: 'text', placeholder: 'Ex : Où dois-je envoyer votre guide ?' },
  prize_name:     { label: 'Lot à gagner',      type: 'text', placeholder: 'Ex : Un bon d\'achat de 200 CHF' },
  prize_value:    { label: 'Valeur du lot',     type: 'text', placeholder: 'Ex : 200 CHF' },
  end_text:       { label: 'Date du tirage',    type: 'text', placeholder: 'Ex : Tirage au sort le 30 juin 2026' },
  rules:          { label: 'Règlement du jeu',  type: 'textarea', placeholder: 'Conditions de participation, RGPD…' },
  accent_color:   { label: 'Couleur principale', type: 'color' },
};

export default function LandingBuilder({ strategyInstanceId, actionId, existingPage, onClose, onPublished }) {
  const { companyProfile } = useApp();
  const [step, setStep]     = useState(existingPage ? 2 : 1); // 1=template, 2=edit, 3=published
  const [tplId, setTplId]   = useState(existingPage?.template || 'lead_magnet');
  const [config, setConfig] = useState(() => {
    const tpl = getTemplate(existingPage?.template || 'lead_magnet');
    const base = { ...tpl.defaultConfig };
    // Pre-fill company info
    if (companyProfile?.name)    base.company_name = companyProfile.name;
    if (companyProfile?.logo_url) base.logo_url    = companyProfile.logo_url;
    return existingPage?.config ? { ...base, ...existingPage.config } : base;
  });
  const [title, setTitle]   = useState(existingPage?.title || config.headline || '');
  // ✏️ Édition en place : historique (undo/redo) + version Appee + device
  const [history, setHistory] = useState({ past: [], future: [] });
  const initialConfigRef = useRef(null);
  const [device, setDevice] = useState('desktop'); // desktop | mobile
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [page, setPage]     = useState(existingPage || null); // saved page object
  const [copied, setCopied] = useState(false);
  const [stats, setStats]   = useState(null);
  const [showLeads, setShowLeads] = useState(false);
  const [leads, setLeads]   = useState([]);
  const [winner, setWinner] = useState(null);
  const [drawing, setDrawing] = useState(false);

  const isConcours = tplId === 'concours';

  const handleDrawWinner = async () => {
    if (!page?.id) return;
    setDrawing(true);
    try {
      const res = await api.drawLandingPageWinner(page.id);
      if (res.success) {
        setWinner(res.winner);
      } else {
        alert(res.message || 'Aucun participant pour le tirage.');
      }
    } catch (e) {
      alert('Erreur lors du tirage : ' + e.message);
    } finally {
      setDrawing(false);
    }
  };

  // Sync title with headline
  useEffect(() => {
    if (!title || title === config.headline) return;
    // intentionally don't auto-sync to avoid overwriting
  }, [config.headline]);

  const tpl = getTemplate(tplId);

  // Load stats if page already exists & published
  useEffect(() => {
    if (page?.id && page?.published_at) {
      api.getLandingPageStats(page.id).then(data => {
        setStats(data);
        // Récupérer le gagnant déjà tiré, le cas échéant
        const w = (data.leads || []).find(l => l.is_winner);
        if (w) setWinner(w);
      }).catch(() => {});
    }
  }, [page]);

  const handleSelectTemplate = (id) => {
    const newTpl = getTemplate(id);
    setTplId(id);
    const base = { ...newTpl.defaultConfig };
    if (companyProfile?.name)     base.company_name = companyProfile.name;
    if (companyProfile?.logo_url)  base.logo_url    = companyProfile.logo_url;
    setConfig(base);
    setTitle(base.headline);
    setStep(2);
  };

  const setField = (key, val) => setConfig(c => ({ ...c, [key]: val }));
  const setBullet = (i, val) => setConfig(c => {
    const bullets = [...(c.bullets || [])];
    bullets[i] = val;
    return { ...c, bullets };
  });
  const addBullet    = () => setConfig(c => ({ ...c, bullets: [...(c.bullets || []), ''] }));
  const removeBullet = (i) => setConfig(c => ({ ...c, bullets: (c.bullets || []).filter((_, j) => j !== i) }));

  const handleSaveDraft = useCallback(async () => {
    setSaving(true);
    try {
      const payload = { title: title || config.headline, template: tplId, config, strategy_instance_id: strategyInstanceId, action_id: actionId };
      let saved;
      if (page?.id) {
        saved = await api.updateLandingPage(page.id, payload);
      } else {
        saved = await api.createLandingPage(payload);
      }
      setPage(saved);
    } catch (e) {
      alert('Erreur lors de la sauvegarde : ' + e.message);
    } finally {
      setSaving(false);
    }
  }, [title, config, tplId, page, strategyInstanceId, actionId]);

  const handlePublish = async () => {
    setPublishing(true);
    try {
      let saved = page;
      if (!saved?.id) {
        const payload = { title: title || config.headline, template: tplId, config, strategy_instance_id: strategyInstanceId, action_id: actionId };
        saved = await api.createLandingPage(payload);
        setPage(saved);
      } else {
        // save latest config first
        saved = await api.updateLandingPage(saved.id, { title: title || config.headline, template: tplId, config });
        setPage(saved);
      }
      const published = await api.publishLandingPage(saved.id);
      setPage(published);
      if (onPublished) onPublished(published);
      setStep(3);
    } catch (e) {
      alert('Erreur lors de la publication : ' + e.message);
    } finally {
      setPublishing(false);
    }
  };

  const handleUnpublish = async () => {
    if (!page?.id) return;
    try {
      const updated = await api.unpublishLandingPage(page.id);
      setPage(updated);
      setStep(2);
    } catch (e) {
      alert('Erreur : ' + e.message);
    }
  };

  const publicUrl = page?.slug ? `${PUBLIC_BASE}/lp/${page.slug}` : null;

  const copyUrl = () => {
    if (!publicUrl) return;
    navigator.clipboard.writeText(publicUrl).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const loadLeads = async () => {
    if (!page?.id) return;
    try {
      const data = await api.getLandingPageLeads(page.id);
      setLeads(data.leads || []);
      setShowLeads(true);
    } catch {}
  };

  const PreviewComp = RENDERERS[tplId] || RENDERERS.lead_magnet;

  if (initialConfigRef.current === null) initialConfigRef.current = JSON.parse(JSON.stringify(config));

  const editField = (k, v) => {
    setHistory(h => ({ past: [...h.past.slice(-49), config], future: [] }));
    setConfig(c => ({ ...c, [k]: v }));
  };
  const undo = () => setHistory(h => {
    if (!h.past.length) return h;
    const prev = h.past[h.past.length - 1];
    setConfig(prev);
    return { past: h.past.slice(0, -1), future: [config, ...h.future].slice(0, 50) };
  });
  const redo = () => setHistory(h => {
    if (!h.future.length) return h;
    const next = h.future[0];
    setConfig(next);
    return { past: [...h.past, config].slice(-50), future: h.future.slice(1) };
  });
  const restore = () => {
    setHistory(h => ({ past: [...h.past.slice(-49), config], future: [] }));
    setConfig(JSON.parse(JSON.stringify(initialConfigRef.current)));
  };

  return (
    <div className="lb-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="lb-modal">

        {/* Header */}
        <div className="lb-header">
          <div className="lb-header-left">
            <div className="lb-header-title">🌐 Landing Page</div>
            <div className="lb-header-sub">
              {step === 1 ? 'Choisir un template' : step === 2 ? `${tpl.icon} ${tpl.name}` : '✅ Publiée'}
            </div>
          </div>
          <div className="lb-header-steps">
            {[1,2,3].map(s => (
              <div key={s} className={`lb-step${step >= s ? ' active' : ''}${step > s ? ' done' : ''}`}>
                {step > s ? '✓' : s}
              </div>
            ))}
          </div>
          <button className="lb-close" onClick={onClose}>✕</button>
        </div>

        {/* ── Step 1: Template picker ── */}
        {step === 1 && (
          <div className="lb-body lb-templates">
            <div className="lb-templates-title">Quel type de landing page voulez-vous créer ?</div>
            <div className="lb-templates-grid">
              {TEMPLATES.map(t => (
                <button key={t.id} className="lb-tpl-card" onClick={() => handleSelectTemplate(t.id)}>
                  <div className="lb-tpl-hero" style={{ background: t.bg }}>
                    <span style={{ fontSize: 36 }}>{t.icon}</span>
                  </div>
                  <div className="lb-tpl-info">
                    <div className="lb-tpl-name">{t.name}</div>
                    <div className="lb-tpl-desc">{t.desc}</div>
                  </div>
                  <div className="lb-tpl-arrow">→</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Step 2: Editor ── */}
        {step === 2 && (
          <div className="lb-body lb-editor">
            {/* Left: form fields */}
            <div className="lb-fields">
              {tplId === 'concours' && (() => {
                const sc = scoreConcours(config);
                const col = sc.score >= 80 ? '#10B981' : sc.score >= 60 ? '#F59E0B' : '#EF4444';
                const missing = sc.items.filter(i => !i.ok);
                return (
                  <div style={{ background: '#fff', border: `2px solid ${col}44`, borderRadius: 14, padding: '13px 16px', marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 22, fontWeight: 900, color: col }}>{sc.score}<span style={{ fontSize: 12, color: '#9CA3AF' }}>/100</span></span>
                      <b style={{ fontSize: 13.5 }}>Score de campagne</b>
                      <span style={{ fontSize: 11.5, color: '#6B7280' }}>{sc.score >= 80 ? 'Prête à cartonner 🎉' : sc.score >= 60 ? 'Bonne base — quelques réglages' : 'À muscler avant de publier'}</span>
                    </div>
                    {missing.length > 0 && (
                      <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {missing.map((i, idx) => (
                          <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12, color: '#374151', lineHeight: 1.45 }}>
                            <span style={{ color: col }}>▸</span>
                            <span style={{ flex: 1 }}><b>{i.label}</b> (+{i.max} pts) — {i.fix}</span>
                            {i.apply && (
                              <button type="button" onClick={() => setConfig(c => i.apply(c))}
                                style={{ border: 'none', background: 'var(--violet, #6C5CE7)', color: '#fff', borderRadius: 999, padding: '3px 11px', fontSize: 11, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                                Appliquer
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
              <div className="lb-section-title">🏷️ Titre de la page (interne)</div>
              <input
                className="lb-input"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="Nom interne (non visible par les visiteurs)"
              />

              <div className="lb-section-title" style={{ marginTop: 20 }}>✏️ Contenu</div>
              {tpl.fields.filter(f => f !== 'bullets' && f !== 'accent_color').map(f => {
                const meta = FIELD_LABELS[f];
                if (!meta) return null;
                return (
                  <div key={f} className="lb-field">
                    <label className="lb-label">{meta.label}</label>
                    {meta.type === 'textarea' ? (
                      <textarea
                        className="lb-textarea"
                        value={config[f] || ''}
                        onChange={e => setField(f, e.target.value)}
                        placeholder={meta.placeholder}
                        rows={3}
                      />
                    ) : (
                      <input
                        className="lb-input"
                        type="text"
                        value={config[f] || ''}
                        onChange={e => setField(f, e.target.value)}
                        placeholder={meta.placeholder}
                      />
                    )}
                  </div>
                );
              })}

              {/* Bullets */}
              <div className="lb-section-title" style={{ marginTop: 20 }}>✅ Arguments clés (coches)</div>
              {(config.bullets || []).map((b, i) => (
                <div key={i} className="lb-bullet-row">
                  <input
                    className="lb-input"
                    value={b}
                    onChange={e => setBullet(i, e.target.value)}
                    placeholder={`Argument ${i + 1}`}
                  />
                  <button className="lb-bullet-del" onClick={() => removeBullet(i)}>✕</button>
                </div>
              ))}
              <button className="lb-bullet-add" onClick={addBullet}>+ Ajouter un argument</button>

              {/* Color + form options */}
              <div className="lb-section-title" style={{ marginTop: 20 }}>🎨 Couleur principale</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <input type="color" value={config.accent_color || '#6428F4'} onChange={e => setField('accent_color', e.target.value)} style={{ width: 48, height: 40, border: 'none', borderRadius: 8, cursor: 'pointer' }} />
                <span style={{ fontSize: 13, color: '#6B7280' }}>{config.accent_color}</span>
              </div>

              <div className="lb-section-title" style={{ marginTop: 20 }}>📋 Champs du formulaire</div>
              {[
                { key: 'show_name',      label: 'Demander le prénom/nom' },
                { key: 'show_birthdate', label: '🎂 Demander la date de naissance (recommandé pour les anniversaires)' },
                { key: 'show_phone',     label: 'Demander le téléphone' },
                { key: 'show_address',   label: 'Demander l\'adresse postale' },
                { key: 'show_message',   label: 'Demander un message' },
              ].map(opt => (
                <label key={opt.key} className="lb-checkbox-row">
                  <input type="checkbox" checked={!!config[opt.key]} onChange={e => setField(opt.key, e.target.checked)} />
                  {opt.label}
                </label>
              ))}
              {config.show_birthdate && (
                <label className="lb-checkbox-row" style={{ marginLeft: 24, opacity: 0.9 }}>
                  <input type="checkbox" checked={!!config.birthdate_required} onChange={e => setField('birthdate_required', e.target.checked)} />
                  Rendre la date de naissance obligatoire
                </label>
              )}

              <div className="lb-section-title" style={{ marginTop: 20 }}>🏢 Informations entreprise</div>
              <div className="lb-field">
                <label className="lb-label">Nom de l'entreprise</label>
                <input className="lb-input" value={config.company_name || ''} onChange={e => setField('company_name', e.target.value)} placeholder="Mon entreprise" />
              </div>
              <div className="lb-field">
                <label className="lb-label">URL du logo (optionnel)</label>
                <input className="lb-input" value={config.logo_url || ''} onChange={e => setField('logo_url', e.target.value)} placeholder="https://monsite.com/logo.png" />
              </div>
            </div>

            {/* Right: live preview */}
            <div className="lb-preview-pane">
              <div className="lb-preview-label" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span>👁️ Aperçu — ✏️ clique un texte pour le modifier</span>
                <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                  <button type="button" onClick={undo} disabled={!history.past.length} title="Annuler"
                    style={{ border: '1px solid #E8E5F4', background: '#fff', borderRadius: 8, padding: '3px 9px', fontSize: 12, cursor: history.past.length ? 'pointer' : 'default', opacity: history.past.length ? 1 : .4 }}>↩️</button>
                  <button type="button" onClick={redo} disabled={!history.future.length} title="Rétablir"
                    style={{ border: '1px solid #E8E5F4', background: '#fff', borderRadius: 8, padding: '3px 9px', fontSize: 12, cursor: history.future.length ? 'pointer' : 'default', opacity: history.future.length ? 1 : .4 }}>↪️</button>
                  <button type="button" onClick={restore} title="Restaurer la version Appee"
                    style={{ border: '1px solid #E8E5F4', background: '#fff', borderRadius: 8, padding: '3px 9px', fontSize: 12, cursor: 'pointer' }}>🔄</button>
                  <button type="button" onClick={() => setDevice(d => d === 'mobile' ? 'desktop' : 'mobile')} title="Basculer mobile / desktop"
                    style={{ border: '1px solid #E8E5F4', background: device === 'mobile' ? '#EDE9FF' : '#fff', borderRadius: 8, padding: '3px 9px', fontSize: 12, cursor: 'pointer' }}>{device === 'mobile' ? '📱' : '💻'}</button>
                </span>
              </div>
              <div className="lb-preview-frame">
                <div className="lb-preview-scale" style={device === 'mobile' ? { maxWidth: 375, margin: '0 auto', border: '10px solid #111', borderRadius: 28, overflow: 'hidden' } : undefined}>
                  <PreviewComp config={config} onSubmit={() => {}} submitting={false} success={false} preview={true} onEditField={editField} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Step 3: Published ── */}
        {step === 3 && page && (
          <div className="lb-body lb-published">
            <div className="lb-pub-hero">
              <div className="lb-pub-icon">🚀</div>
              <div className="lb-pub-title">Votre landing page est en ligne !</div>
              <div className="lb-pub-sub">Partagez ce lien pour capturer des leads</div>
            </div>
            <div className="lb-pub-url-row">
              <input className="lb-pub-url" value={publicUrl || ''} readOnly />
              <button className={`lb-pub-copy${copied ? ' copied' : ''}`} onClick={copyUrl}>
                {copied ? '✓ Copié !' : '📋 Copier'}
              </button>
              {publicUrl && <a href={publicUrl} target="_blank" rel="noreferrer" className="lb-pub-open">🔗 Ouvrir →</a>}
            </div>

            {/* Stats */}
            {stats && (
              <div className="lb-stats-row">
                <div className="lb-stat-card">
                  <div className="lb-stat-val">{stats.visits_count}</div>
                  <div className="lb-stat-lbl">👁️ Visites</div>
                </div>
                <div className="lb-stat-card lb-stat-leads">
                  <div className="lb-stat-val">{stats.leads_count}</div>
                  <div className="lb-stat-lbl">📬 Leads</div>
                </div>
                {stats.leads_count > 0 && (
                  <div className="lb-stat-card">
                    <div className="lb-stat-val">{stats.visits_count > 0 ? Math.round(stats.leads_count / stats.visits_count * 100) : 0}%</div>
                    <div className="lb-stat-lbl">📊 Conversion</div>
                  </div>
                )}
              </div>
            )}

            {/* Tirage au sort (concours uniquement) */}
            {isConcours && (
              <div className="lb-draw-box">
                {winner ? (
                  <div className="lb-draw-winner">
                    <div className="lb-draw-winner-badge">🏆 Gagnant tiré au sort</div>
                    <div className="lb-draw-winner-name">
                      {winner.name || winner.first_name || winner.email}
                    </div>
                    <div className="lb-draw-winner-contact">
                      ✉️ {winner.email}{winner.phone ? ` · 📞 ${winner.phone}` : ''}
                    </div>
                    <button className="lb-draw-again" onClick={handleDrawWinner} disabled={drawing}>
                      {drawing ? '⏳ Tirage…' : '🔄 Retirer un autre gagnant'}
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="lb-draw-title">🎲 Tirage au sort</div>
                    <div className="lb-draw-sub">
                      {stats?.leads_count > 0
                        ? `${stats.leads_count} participant${stats.leads_count > 1 ? 's' : ''} — prêt pour le tirage`
                        : 'Aucun participant pour le moment'}
                    </div>
                    <button className="lb-draw-btn" onClick={handleDrawWinner} disabled={drawing || !stats?.leads_count}>
                      {drawing ? '⏳ Tirage en cours…' : '🎁 Tirer un gagnant au hasard'}
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Leads */}
            {!showLeads ? (
              stats?.leads_count > 0 && (
                <button className="lb-leads-btn" onClick={loadLeads}>
                  📬 Voir les {stats.leads_count} participant{stats.leads_count > 1 ? 's' : ''} capturé{stats.leads_count > 1 ? 's' : ''} →
                </button>
              )
            ) : (
              <div className="lb-leads-table-wrap">
                <div className="lb-leads-title">📬 Participants capturés</div>
                <table className="lb-leads-table">
                  <thead>
                    <tr><th>Nom</th><th>Email</th><th>🎂 Naissance</th><th>Téléphone</th><th>Date</th></tr>
                  </thead>
                  <tbody>
                    {leads.map((l, i) => (
                      <tr key={i} style={l.is_winner ? { background: '#FEF9C3' } : undefined}>
                        <td>{l.is_winner ? '🏆 ' : ''}{l.name || [l.first_name, l.last_name].filter(Boolean).join(' ') || '—'}</td>
                        <td>{l.email}</td>
                        <td style={{ fontSize: 12 }}>{l.birth_date ? new Date(l.birth_date).toLocaleDateString('fr-FR') : '—'}</td>
                        <td>{l.phone || '—'}</td>
                        <td style={{ fontSize: 11, color: '#9CA3AF' }}>{new Date(l.created_at).toLocaleDateString('fr-FR')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="lb-pub-actions">
              <button className="lb-btn-secondary" onClick={() => setStep(2)}>✏️ Modifier la page</button>
              <button className="lb-btn-danger" onClick={handleUnpublish}>⏸ Dépublier</button>
            </div>
          </div>
        )}

        {/* Footer */}
        {step === 2 && (
          <div className="lb-footer">
            <button className="lb-btn-secondary" onClick={() => setStep(1)}>← Templates</button>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="lb-btn-secondary" onClick={handleSaveDraft} disabled={saving}>
                {saving ? '⏳ Sauvegarde…' : '💾 Enregistrer brouillon'}
              </button>
              <button className="lb-btn-primary" onClick={handlePublish} disabled={publishing}>
                {publishing ? '⏳ Publication…' : page?.published_at ? '🔄 Republier les modifications' : '🚀 Publier & obtenir le lien'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
